import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function BodyFatCalculator() {
  const [gender, setGender] = useState("male");
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [neck, setNeck] = useState("");
  const [waist, setWaist] = useState("");
  const [hip, setHip] = useState("");
  const [bodyFat, setBodyFat] = useState<number | null>(null);

  const calculateBodyFat = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const n = parseFloat(neck);
    const wa = parseFloat(waist);
    const hi = parseFloat(hip);
    
    if (!w || !h || !n || !wa || w <= 0 || h <= 0 || n <= 0 || wa <= 0) return;
    if (gender === "female" && (!hi || hi <= 0)) return;
    
    let bf: number;
    if (gender === "male") {
      bf = 495 / (1.0324 - 0.19077 * Math.log10(wa - n) + 0.15456 * Math.log10(h)) - 450;
    } else {
      bf = 495 / (1.29579 - 0.35004 * Math.log10(wa + hi - n) + 0.22100 * Math.log10(h)) - 450;
    }
    
    setBodyFat(parseFloat(bf.toFixed(1)));
  };

  return (
    <PageLayout calculatorName="Body Fat Percentage Calculator" calculatorPath="/calculator/body-fat">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate Body Fat</CardTitle>
            <CardDescription>U.S. Navy Method</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Gender</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Age (years)</Label>
              <Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="25" />
            </div>
            <div>
              <Label>Weight (kg)</Label>
              <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="70" />
            </div>
            <div>
              <Label>Height (cm)</Label>
              <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="175" />
            </div>
            <div>
              <Label>Neck Circumference (cm)</Label>
              <Input type="number" value={neck} onChange={(e) => setNeck(e.target.value)} placeholder="38" />
            </div>
            <div>
              <Label>Waist Circumference (cm)</Label>
              <Input type="number" value={waist} onChange={(e) => setWaist(e.target.value)} placeholder="85" />
            </div>
            {gender === "female" && (
              <div>
                <Label>Hip Circumference (cm)</Label>
                <Input type="number" value={hip} onChange={(e) => setHip(e.target.value)} placeholder="95" />
              </div>
            )}
            <Button onClick={calculateBodyFat} className="w-full">Calculate</Button>
          </CardContent>
        </Card>

        {bodyFat !== null && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Body Fat Percentage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold text-primary mb-2">{bodyFat}%</div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="font-semibold mb-2">Categories:</div>
                  <div className="flex justify-between">
                    <span>Essential Fat:</span>
                    <span className="text-muted-foreground">{gender === "male" ? "2-5%" : "10-13%"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Athletes:</span>
                    <span className="text-muted-foreground">{gender === "male" ? "6-13%" : "14-20%"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Fitness:</span>
                    <span className="text-muted-foreground">{gender === "male" ? "14-17%" : "21-24%"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Average:</span>
                    <span className="text-muted-foreground">{gender === "male" ? "18-24%" : "25-31%"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Obese:</span>
                    <span className="text-muted-foreground">{gender === "male" ? "25%+" : "32%+"}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="md:col-span-2">
              <CalculatorSuperCalcCTA config={calculatorCTAs["body-fat"]} trigger={bodyFat} />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
